<script setup lang="ts">
import type { ShortsSectionVariant } from './variants';
import type { ShortVideo } from '~/composables/shorts';
import type { ShortsFilters } from '~/core/entities/property/filters';

import { cn, useStaleGuard } from '@/utils';
import {
	breakpointsTailwind,
	useBreakpoints,
	useIntersectionObserver
} from '@vueuse/core';
import { useEvents } from '~/composables/events/useEvents';
import { useFetchShorts, useLastVideoState } from '~/composables/shorts';
import { MixpanelProps } from '~/core/service/mixpanel';
import { isEqual } from 'ohash';

import { VideoCard } from '~/components/VideoCard';

import SectionSkeleton from './SectionSkeleton.vue';
import { getSectionStyles } from './variants';

const MAX_VIDEOS = 15;

const props = withDefaults(
	defineProps<{
		title: string;
		filters?: ShortsFilters;
		variant?: ShortsSectionVariant;
		showTitleIcon?: boolean;
	}>(),
	{
		variant: 'dark',
		showTitleIcon: true
	}
);

const styles = computed(() => getSectionStyles(props.variant));

const carouselOptions = computed(() => ({
	cyclic: true,
	class: styles.value.contentGap
}));

const emit = defineEmits<{
	(e: 'update:has-videos', value: boolean): void;
}>();

const { fetchVideos } = useFetchShorts();

const allVideos = ref<ShortVideo[]>([]);
const isLoading = ref(false);
const isPreloaded = ref(false);

const videos = computed(() => allVideos.value.slice(0, MAX_VIDEOS));

const isMobileOrTablet = ref(false);
const sectionRef = ref<HTMLElement | null>(null);
const isSectionVisible = ref(false);

const sliderRef = ref<{
	currentCarouselItemIndex: number;
	scrollTo: (index: number) => void;
} | null>(null);

const shouldAutoplay = computed(
	() => isMobileOrTablet.value && isSectionVisible.value
);

const hasVideos = computed(() => videos.value.length > 0);

const shouldShowSection = computed(
	() => !isPreloaded.value || isLoading.value || hasVideos.value
);

const fetchGuard = useStaleGuard();

async function loadVideos(filters: ShortsFilters): Promise<void> {
	const capturedId = fetchGuard.next();
	isLoading.value = true;
	try {
		const result = await fetchVideos(filters);
		if (fetchGuard.isStale(capturedId)) return;
		allVideos.value = result.items;
		isPreloaded.value = true;
	} finally {
		if (!fetchGuard.isStale(capturedId)) {
			isLoading.value = false;
		}
	}
}

onMounted(() => {
	const breakpoints = useBreakpoints(breakpointsTailwind);
	isMobileOrTablet.value = breakpoints.smaller('lg').value;

	watch(breakpoints.smaller('lg'), val => {
		isMobileOrTablet.value = val;
	});

	useIntersectionObserver(
		sectionRef,
		entries => {
			const entry = entries[0];
			if (entry) {
				isSectionVisible.value = entry.isIntersecting;
			}
		},
		{ threshold: 0.3 }
	);

	requestIdleCallback(() => {
		loadVideos(props.filters ?? {});
	});
});

watch(
	() => props.filters,
	(newFilters, oldFilters) => {
		if (isEqual(newFilters, oldFilters)) return;
		loadVideos(newFilters ?? {});
	},
	{ deep: true }
);

watch(
	hasVideos,
	value => {
		emit('update:has-videos', value);
	},
	{ immediate: true }
);

const route = useRoute();
const { trackCtaViewMoreClicked } = useEvents();
const lastVideoState = useLastVideoState();

function handleVideoCardClick(index: number): void {
	const item = videos.value[index];
	if (!item) return;

	lastVideoState.save(item.property.commercialId, index);
	navigateTo(`/shorts/${item.property.commercialId}`);
}

function handleCtaClick(): void {
	trackCtaViewMoreClicked({
		[MixpanelProps.CTA_TEXT]: 'Explorar todos os Shorts',
		[MixpanelProps.CURRENT_PAGE]: route.fullPath,
		[MixpanelProps.SECTION_NAME]: 'shorts',
		[MixpanelProps.DESTINATION_URL]: '/shorts'
	});
	navigateTo('/shorts');
}
</script>

<template>
	<SectionSkeleton
		v-if="shouldShowSection && !isPreloaded"
		:title="title"
		:variant="variant"
	/>

	<section
		v-else-if="shouldShowSection"
		ref="sectionRef"
		:class="styles.section"
	>
		<StandardSlider
			ref="sliderRef"
			:items="videos"
			:is-loading="false"
			:carousel-item-class="styles.carouselItemClass"
			:carousel-options="carouselOptions"
			:limit-photos="false"
			section-name="shorts"
			:container-class="styles.containerClass"
			:carousel-content-class="styles.carouselContentClass"
			:selector-variant="styles.selectorVariant"
			:selector-arrows-variant="styles.selectorArrowsVariant"
			:show-navigation-arrows="false"
		>
			<template #header>
				<header :class="styles.headerClass">
					<div class="flex w-full flex-col items-start justify-between">
						<div class="flex items-center gap-3">
							<IconMaterialSymbolsLightPlayCircle
								v-if="showTitleIcon"
								:class="cn('size-7 md:size-12', styles.titleText)"
							/>
							<h2 :class="cn(styles.titleFont, styles.titleText)">
								{{ title }}
							</h2>
						</div>
					</div>
				</header>
			</template>

			<template #items="{ item, index }">
				<VideoCard
					:property="item.property"
					:thumbnail-url="item.thumbnailUrl"
					:video-url="item.videoUrl"
					:index="index"
					section-name="shorts hero section"
					:is-active="
						shouldAutoplay && sliderRef?.currentCarouselItemIndex === index
					"
					:hover-to-play="!isMobileOrTablet"
					@click="handleVideoCardClick"
				/>
			</template>

			<template #cta>
				<Button
					v-if="styles.showCtaButton"
					:variant="styles.buttonVariant"
					size="responsive"
					class="text-base font-medium"
					@click="handleCtaClick"
				>
					<span>Explorar todos os Shorts</span>
					<IconMaterialSymbolsLightNorthEast
						:class="cn('size-5 xl:size-6', styles.ctaArrowClass)"
					/>
				</Button>
			</template>
		</StandardSlider>
	</section>
</template>
